import { useMutation } from '@tanstack/react-query';
import { renameUser } from 'api/userApi';
import { queryClient } from 'App';
import FalconCardHeader from 'components/common/FalconCardHeader';
import LoadingButton from 'components/fantaclasse/LoadingButton';
import { useUser } from 'context/UserContext';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useDebounce } from 'use-debounce';
import * as Yup from 'yup';

const ProfileSettings = () => {
  const user = useUser();
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameNotAvaiableError, setUsernameNotAvaiableError] =
    useState(null);

  const formik = useFormik({
    initialValues: {
      username: user.username
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .matches(
          /^[a-zA-Z0-9_.]+$/,
          "L'username può contenere solo lettere, numeri, punti e trattini bassi."
        )
        .min(4, "L'username deve contenere almeno 4 caratteri.")
        .max(30, "L'username deve contenere al massimo 30 caratteri.")
        .required("L'username è obbligatorio.")
    }),
    validateOnChange: true,
    onSubmit: async values => {
      updateUserMutation.mutate(values.username);
    }
  });

  const [isUpdating, setIsUpdating] = useState(false);
  const updateUserMutation = useMutation({
    mutationFn: renameUser,
    onMutate: () => {
      setIsUpdating(true);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['userData'] });
      toast.success(() => (
        <span>
          Il tuo username è stato modificato in <strong>{data.username}</strong>
        </span>
      ));
    },
    onError: err => {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Errore durante la modifica dell'username"
      );
    },
    onSettled: () => {
      setIsUpdating(false);
    }
  });

  const [debouncedUsername] = useDebounce(formik.values.username, 800);

  useEffect(() => {
    if (!formik.isValid) {
      setIsCheckingUsername(false);
      return;
    }
    if (debouncedUsername && debouncedUsername !== user.username) {
      // quindi se l'username è uguale a quello attuale, non fa nulla ma resta
      // setIsCheckingUsername a true avviato nel onChange, quindi il button è
      // disabilitato.
      const checkUsernameAvailability = async () => {
        setIsCheckingUsername(true);
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/check/username/${debouncedUsername}`
          );
          if (response.status === 200) {
            setUsernameNotAvaiableError(null);
          } else if (response.status === 422) {
            setUsernameNotAvaiableError("L'username non è disponibile.");
          } else {
            toast.error('Errore di validazione del nome utente');
          }
        } catch (error) {
          toast.error(
            'Errore di rete durante il controllo della disponibilità del nome utente'
          );
        } finally {
          setIsCheckingUsername(false);
        }
      };

      checkUsernameAvailability();
    }
  }, [debouncedUsername]);

  const handleSubmit = e => {
    e.preventDefault();
    formik.handleSubmit(e);
  };

  return (
    <Card className="mb-3">
      <FalconCardHeader title="Profilo" />
      <Card.Body className="bg-body-tertiary">
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3 g-3">
            <Form.Group as={Col} controlId="username">
              <Form.Label>Username</Form.Label>
              <Form.Control
                name="username"
                type="text"
                value={formik.values.username}
                onChange={e => {
                  setIsCheckingUsername(true);
                  formik.handleChange(e);
                }}
                onBlur={formik.handleBlur}
                placeholder="Username"
                minLength={4}
                maxLength={30}
                pattern="[a-zA-Z0-9_.]+"
                isValid={
                  formik.values.username !== user.username &&
                  !isCheckingUsername &&
                  formik.touched.username &&
                  !formik.errors.username &&
                  !usernameNotAvaiableError
                }
                isInvalid={
                  formik.values.username !== user.username &&
                  !isCheckingUsername &&
                  formik.touched.username &&
                  (formik.errors.username || usernameNotAvaiableError)
                }
                required
              />
              <Form.Control.Feedback>
                L'username è disponibile.
              </Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {formik.errors.username || usernameNotAvaiableError}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
          <div className="text-end">
            <LoadingButton
              text="Salva"
              isLoading={isUpdating}
              disabled={
                formik.values.username === user.username ||
                !formik.isValid ||
                isCheckingUsername ||
                usernameNotAvaiableError
              }
              type="submit"
            />
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default ProfileSettings;
